import React from "react";

import { defineMessage, useIntl } from "react-intl";

import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { FormItem, TextField, TextFieldProps } from "@mapmycustomers/ui";

import useActivityNameValidationRules from "@app/component/activity/utils/useActivityNameValidationRules";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";

interface ActivityNameFieldProps extends Pick<
  TextFieldProps,
  "caption" | "disabled" | "placeholder" | "required"
> {
  activityTypes: ActivityType[];
  onChange?(name: string): void;
  title?: string;
}

const label = defineMessage({
  id: "createEditActivityModal.field.name",
  defaultMessage: "Activity Name",
  description: "Activity name field on the Create Activity modal",
});

const ActivityNameField = ({
  activityTypes,
  caption,
  disabled,
  onChange,
  placeholder,
  required,
  title,
}: ActivityNameFieldProps) => {
  const intl = useIntl();

  const [nameFocused, setNameFocused, resetNameFocused] = useBoolean();
  const activityNameValidationRules = useActivityNameValidationRules(intl);

  return (
    <FormItem dependencies={[ActivityFieldName.NAME, "activityTypeId"]} noStyle>
      {(form) => {
        const name = form.getFieldValue(ActivityFieldName.NAME);
        const activityTypeId = form.getFieldValue("activityTypeId");

        // Name or activity type name. Thing with nameFocused is to allow user to clear field
        // when its focused to allow user enter his own name.
        const nameFieldValue = nameFocused
          ? name
          : name.trim() || activityTypes.find(({ id }) => id === activityTypeId)?.name || "";

        return (
          <FormItem
            dependencies={["activityTypeId"]}
            label={title ?? intl.formatMessage(label)}
            name={ActivityFieldName.NAME}
            rules={required ? activityNameValidationRules : undefined}
            showLabel={false}
          >
            <TextField
              caption={caption}
              disabled={disabled}
              label={intl.formatMessage(label)}
              onBlur={resetNameFocused}
              onChange={onChange}
              onFocus={setNameFocused}
              placeholder={placeholder}
              required={required}
              value={nameFieldValue}
            />
          </FormItem>
        );
      }}
    </FormItem>
  );
};

export default ActivityNameField;
